"use client"

import { useCallback, useEffect, useState } from "react"
import { createClient } from "@/utils/supabase/client"
import { acceptMessagingGuidelines } from "@/lib/chat/accept-messaging-guidelines"

const SAFETY_BANNER_SESSION_KEY = "chat-safety-banner-dismissed"

export function useMessagingGuidelines(userId: string | null) {
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(Boolean(userId))
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    setBannerDismissed(sessionStorage.getItem(SAFETY_BANNER_SESSION_KEY) === "1")
  }, [])

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }
    let active = true
    ;(async () => {
      try {
        const supabase = createClient()
        const { data, error } = await supabase
          .from("profiles")
          .select("messaging_guidelines_accepted_at")
          .eq("id", userId)
          .maybeSingle()
        if (!active) return
        if (error) {
          setAcceptedAt(null)
          setSheetOpen(false)
          return
        }
        const value =
          typeof data?.messaging_guidelines_accepted_at === "string"
            ? data.messaging_guidelines_accepted_at
            : null
        setAcceptedAt(value)
        setSheetOpen(!value)
      } catch {
        if (active) {
          setAcceptedAt(null)
          setSheetOpen(false)
        }
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [userId])

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true)
    if (typeof window !== "undefined") {
      sessionStorage.setItem(SAFETY_BANNER_SESSION_KEY, "1")
    }
  }, [])

  const acceptGuidelines = useCallback(async () => {
    const result = await acceptMessagingGuidelines()
    setAcceptedAt(result.acceptedAt)
    setSheetOpen(false)
  }, [])

  const goBackFromGuidelines = useCallback(() => {
    setSheetOpen(false)
  }, [])

  return {
    loading,
    acceptedAt,
    needsAcceptance: !loading && !acceptedAt,
    sheetOpen,
    setSheetOpen,
    acceptGuidelines,
    goBackFromGuidelines,
    showSafetyBanner: Boolean(acceptedAt) && !bannerDismissed,
    dismissBanner,
  }
}
